import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Building2, Mail, RefreshCw } from 'lucide-react';
import { getApiUrl } from '../utils/api';

const API_URL = getApiUrl();
const fmt = (iso) => (iso ? new Date(iso).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) : '-');

const EnquiryRow = ({ e }) => (
  <tr className="border-t border-slate-100 align-top" data-testid={`corporate-enquiry-row-${e.enquiry_id}`}>
    <td className="px-4 py-3 whitespace-nowrap text-slate-500">{fmt(e.created_at)}</td>
    <td className="px-4 py-3">
      <div className="font-medium text-slate-900">{e.full_name}</div>
      <div className="text-slate-500">{e.role || '-'}</div>
    </td>
    <td className="px-4 py-3">
      <div className="font-medium text-slate-900">{e.company}</div>
      <div className="text-slate-500">Team: {e.team_size || '-'}</div>
    </td>
    <td className="px-4 py-3">
      <a href={`mailto:${e.work_email}`} className="text-indigo-600 hover:underline">{e.work_email}</a>
      <div className="text-slate-500">{e.phone || '-'}</div>
    </td>
    <td className="px-4 py-3 max-w-md whitespace-pre-wrap break-words text-slate-700">{e.message || '-'}</td>
    <td className="px-4 py-3">
      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs ${e.email_sent ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}
        title={e.email_error || ''} data-testid={`corporate-enquiry-email-status-${e.enquiry_id}`}>
        <Mail className="w-3 h-3" />{e.email_sent ? 'Sent' : 'Failed'}
      </span>
    </td>
  </tr>
);

export default function CorporateEnquiriesPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/corporate-enquiries`, { withCredentials: true });
      setRows(data.enquiries || []);
    } catch (err) {
      toast.error('Could not load corporate enquiries');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="p-6 space-y-6" data-testid="corporate-enquiries-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Building2 className="w-6 h-6 text-slate-700" />
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">Corporate Enquiries</h1>
            <p className="text-sm text-slate-500">From the website's /for-corporates form. Each one is also emailed to the founders' office.</p>
          </div>
        </div>
        <button onClick={load} data-testid="corporate-enquiries-refresh-btn"
          className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm hover:bg-slate-50">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm" data-testid="corporate-enquiries-table">
          <thead className="bg-slate-50 text-left text-slate-600">
            <tr>
              <th className="px-4 py-3">Received</th><th className="px-4 py-3">Contact</th>
              <th className="px-4 py-3">Company</th><th className="px-4 py-3">Email / Phone</th>
              <th className="px-4 py-3">Message</th><th className="px-4 py-3">Email</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((e) => <EnquiryRow key={e.enquiry_id} e={e} />)}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500" data-testid="corporate-enquiries-empty">No corporate enquiries yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
